import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { BottomSheetContentLayout, IconButton, Menu, useOverlayContext } from 'seagull';
import { ChevronRight, DocumentCheckmark, Invoice, Plus, PlusCircle, TwoUsers, UserAdd } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { NewServiceType, SelectNewServiceTypeModal } from '~/components/AddServicesButton/SelectNewServiceTypeModal/SelectNewServiceTypeModal';
import { useOpenCreateClientModal } from '~/components/CreateClientModal/hooks/useOpenCreateClientModal';
import { ManageContactModalForVendor } from '~/components/ManageContactModal/ManageContactModalForVendor';
import ServiceBuilder from '~/components/ServiceBuilder/ServiceBuilder';
import { useActiveServiceTemplates } from '~/hooks/reactQuery/queries/useActiveServiceTemplates';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGlobalBillClient } from '~/hooks/useGlobalBillClient';
import { useOpenNewProposal } from '~/hooks/useOpenNewProposal';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { serviceTemplateModel } from '~/models';
import { withDeviceProvider } from '~/providers/DeviceProvider';
export var ItemId = /*#__PURE__*/ function(ItemId) {
    ItemId["CreateClient"] = "create-client";
    ItemId["BillClient"] = "bill-client";
    ItemId["CreateContact"] = "create-contact";
    ItemId["CreateProposal"] = "create-proposal";
    ItemId["CreateService"] = "create-service";
    return ItemId;
}({});
export const getQuickActionsMenuTID = ()=>getTestIds();
function ActualQuickActionsMenu() {
    const { QuickActionsMenuTID } = useMemo(()=>getQuickActionsMenuTID(), []);
    const { refresh: refreshContacts } = useContacts({
        refreshOnMount: true
    });
    const { showBottomSheet, hideBottomSheet, pushBottomSheetPage, popBottomSheetPage, showModal, startLoader, stopLoader, showErrorToast, showSuccessToast } = useOverlayContext();
    const { isLoadingProposalTemplates, openNewProposal } = useOpenNewProposal();
    const openCreateClientModal = useOpenCreateClientModal();
    const { FE_CRM } = useFeatureFlags();
    const { openGlobalBillModal } = useGlobalBillClient();
    const serviceTemplateApi = useServiceTemplateApi();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: quickActionsMenuTexts } = useLocaleKeys('components.quickActionsMenu');
    const { data: serviceTemplates, refresh: refreshServiceTemplates } = useActiveServiceTemplates({
        refreshOnMount: true
    });
    async function createNewService() {
        const newServiceType = await showModal({
            biName: 'select_new_service_type',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            content: (p)=>/*#__PURE__*/ _jsx(SelectNewServiceTypeModal, _object_spread({}, p))
        });
        if (!newServiceType) return;
        hideBottomSheet();
        showModal({
            biName: 'service_builder',
            closeOnEscapeKey: true,
            content: (p)=>{
                var _ref;
                return /*#__PURE__*/ _jsx(ServiceBuilder, _object_spread({
                    serviceOrigin: "template",
                    isCreatingBundle: newServiceType === NewServiceType.Bundle,
                    serviceNameBlacklist: (_ref = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.map((s)=>s.name)) !== null && _ref !== void 0 ? _ref : [],
                    onSave: async (output)=>{
                        const body = serviceTemplateModel.fromService(output);
                        const loaderName = 'createServiceTemplateLoader';
                        startLoader(loaderName);
                        const result = await handlePromise(serviceTemplateApi.create(body));
                        if (result.success) {
                            await refreshServiceTemplates({
                                throwOnError: false
                            });
                            showSuccessToast(commonTexts.toasts.serviceCreated(), 'service-template-created-toast');
                        } else {
                            showErrorToast(commonTexts.toasts.failedToCreateService(), 'create-service-fail-toast');
                        }
                        stopLoader(loaderName);
                        return result;
                    }
                }, p));
            }
        });
    }
    function handleClick() {
        const items = [
            ...FE_CRM ? [
                {
                    id: "create-client",
                    body: quickActionsMenuTexts.createClient(),
                    prefixIcon: /*#__PURE__*/ _jsx(TwoUsers, {}),
                    bi: {
                        name: 'quick_actions_create_client'
                    },
                    onClick: openCreateClientModal
                }
            ] : [],
            {
                id: "bill-client",
                body: quickActionsMenuTexts.billClient(),
                prefixIcon: /*#__PURE__*/ _jsx(Invoice, {}),
                bi: {
                    name: 'quick_actions_bill_client'
                },
                onClick: openGlobalBillModal
            },
            {
                id: "create-contact",
                body: quickActionsMenuTexts.createContact(),
                prefixIcon: /*#__PURE__*/ _jsx(UserAdd, {}),
                bi: {
                    name: 'quick_actions_create_contact'
                },
                onClick: ()=>{
                    pushBottomSheetPage({
                        content: /*#__PURE__*/ _jsx(ManageContactModalForVendor, {
                            testId: "manage-contact-modal",
                            onClose: (savedContact)=>{
                                refreshContacts();
                                savedContact ? hideBottomSheet() : popBottomSheetPage();
                            },
                            origin: "Quick actions menu"
                        })
                    });
                }
            },
            {
                id: "create-proposal",
                body: quickActionsMenuTexts.createProposal(),
                disabled: isLoadingProposalTemplates,
                prefixIcon: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                bi: {
                    name: 'quick_actions_create_proposal'
                },
                onClick: ()=>openNewProposal()
            },
            {
                id: "create-service",
                body: quickActionsMenuTexts.createService(),
                prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                bi: {
                    name: 'quick_actions_create_service'
                },
                onClick: createNewService
            }
        ].map((item)=>_object_spread_props(_object_spread({}, item), {
                testId: QuickActionsMenuTID.Item(item.id),
                suffixIcon: /*#__PURE__*/ _jsx(ChevronRight, {})
            }));
        showBottomSheet({
            content: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                header: {
                    title: quickActionsMenuTexts.quickActions()
                },
                children: /*#__PURE__*/ _jsx(Menu, {
                    dimensions: {
                        width: 'auto'
                    },
                    items: items
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(IconButton, {
        bi: {
            name: 'open_quick_actions_menu'
        },
        skin: "primaryBoxed",
        size: "smallMedium",
        testId: QuickActionsMenuTID.Button(),
        onClick: handleClick,
        children: /*#__PURE__*/ _jsx(Plus, {
            size: 16
        })
    });
}
export const QuickActionsMenu = withDeviceProvider(ActualQuickActionsMenu);
